/**
 * ProviderComparisonCard — the dispatch comparison table for a shipment in the
 * "Decide" state. One row per provider contacted, fields as columns, so it
 * scales vertically as the carrier set grows (the load-board BCC pattern can
 * reach 20–30) where a provider-per-column layout would force horizontal scroll.
 *
 * It derives straight from `shipment.providers` (see deriveProviders in
 * shipmentAdapter.js), so it always reflects the current quote state and
 * updates as replies land. This replaces the stacked quote cards in the Decide
 * state (ActivityLog suppresses those once this table is showing).
 *
 * Marq does NOT recommend a carrier — dispatch decides. The table presents the
 * facts and stays scannable: factual pills only (Lowest price, Fastest,
 * Outlier), missing optional fields show "—", and a row expands to the
 * source-verify view (rate breakdown + the provider's raw reply + Accept).
 *
 * Columns are dynamic: an optional field (transit, valid-until, equipment, rate
 * type) only gets a column when at least one provider reported it — a column of
 * dashes is noise.
 *
 * Two action modes:
 *   - Curate/share mode (onShare): a checkbox per responded row to keep/drop
 *     which quotes go into the shared shortlist, and a generic "Share" action in
 *     the footer (the same platform Share pattern used elsewhere in Marteu).
 *   - Accept (onAccept): a per-row Accept button inside the expanded reply view
 *     — dispatch picks a winner. Available in both modes.
 *
 * Renders nothing until there are at least two contacted providers and at
 * least one has responded — a single row isn't a comparison.
 */

import { useState, useEffect, Fragment } from "react";
import {
	ChevronDown,
	ChevronUp,
	Check,
	AlertTriangle,
	Share2,
	Mail,
	FileText,
} from "lucide-react";

const isResponded = (p) => p.status === "received" || p.status === "selected";

const costOf = (p) => {
	// total_cost arrives as a PG numeric string ("1233.00") — coerce before
	// comparing or formatting.
	const n = Number(p.cost);
	return Number.isFinite(n) && String(p.cost ?? "").trim() !== "" ? n : null;
};

const formatCost = (p) => {
	const n = costOf(p);
	if (n == null) return "—";
	const formatted = `$${n.toLocaleString()}`;
	return p.currency && p.currency !== "USD"
		? `${formatted} ${p.currency}`
		: formatted;
};

const formatDate = (value) => {
	if (!value) return "—";
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const hasFuel = (p) => p.fuel != null && String(p.fuel).trim() !== "";

// Rate type comes through the parsed `terms` ("Rate type" → value). It feeds the
// optional RATE TYPE column and is shown only when at least one provider has it.
const rateTypeOf = (p) => {
	if (!Array.isArray(p.terms)) return null;
	const row = p.terms.find(
		([k]) => String(k).toLowerCase() === "rate type",
	);
	return row ? row[1] : null;
};

function sortProviders(rows) {
	// Status order: answered quotes lead (selected first, then received cheapest
	// on top), still-pending next, declined last.
	const STATUS_ORDER = { selected: 0, received: 1, pending: 2, declined: 3 };
	return [...rows].sort((a, b) => {
		const orderDelta =
			(STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
		if (orderDelta !== 0) return orderDelta;
		const ca = costOf(a);
		const cb = costOf(b);
		if (ca != null && cb != null) return ca - cb;
		if (ca != null) return -1;
		if (cb != null) return 1;
		return (a.name || "").localeCompare(b.name || "");
	});
}

// The below-cluster outlier is the cheapest received quote that undercuts the
// next-cheapest by >15% — the classic untrusted-underbidder. Plus whether peers
// reported fuel (drives the no-fuel-line caution).
function buildFlagCtx(sorted) {
	const receivedSorted = sorted
		.filter((p) => isResponded(p) && costOf(p) != null)
		.sort((a, b) => costOf(a) - costOf(b));
	let belowClusterId = null;
	let belowClusterPct = 0;
	let belowClusterDelta = 0;
	if (receivedSorted.length >= 2) {
		const cheapest = costOf(receivedSorted[0]);
		const second = costOf(receivedSorted[1]);
		if (cheapest / second < 0.85) {
			belowClusterId = receivedSorted[0].id || receivedSorted[0].name;
			belowClusterPct = Math.round((1 - cheapest / second) * 100);
			belowClusterDelta = second - cheapest;
		}
	}
	return {
		belowClusterId,
		belowClusterPct,
		belowClusterDelta,
		peersHaveFuel: receivedSorted.some(hasFuel),
	};
}

// Shared analysis: sorted rows + the table-wide flag context (incl. the
// below-cluster outlier). Marq does not recommend a carrier — dispatch decides;
// the table only orders rows and surfaces factual cautions.
export function analyzeProviders(providers) {
	const rows = Array.isArray(providers) ? providers : [];
	const sorted = sortProviders(rows);
	const flagCtx = buildFlagCtx(sorted);
	const outlier = flagCtx.belowClusterId
		? sorted.find((p) => (p.id || p.name) === flagCtx.belowClusterId)
		: null;
	return { rows, sorted, flagCtx, outlier };
}

// Factual pill. No recommendation tone — green = a fact in the carrier's favour
// (cheapest / fastest), orange = a caution to check (outlier).
function Pill({ tone = "neutral", children }) {
	return (
		<span className={`ws-pcompare-pill is-${tone}`}>{children}</span>
	);
}

function BreakdownItem({ label, value, bold }) {
	return (
		<div className="ws-pcompare-bk-item">
			<span className="ws-pcompare-bk-label">{label}</span>
			<span className={`ws-pcompare-bk-value${bold ? " is-bold" : ""}`}>
				{value}
			</span>
		</div>
	);
}

export default function ProviderComparisonCard({
	providers,
	onAccept,
	onShare,
	onSelectionChange,
	onViewPdf,
	viewingPdfQuoteId,
	acceptingQuoteId,
	selectionLocked = false,
}) {
	const rows = Array.isArray(providers) ? providers : [];
	const responded = rows.filter(isResponded);
	const noReply = rows.filter((p) => p.status === "pending");
	const declined = rows.filter((p) => p.status === "declined");
	const [expandedIds, setExpandedIds] = useState(() => new Set());
	// Curate-to-share selection: default to keeping every responded quote, so
	// dispatch drops the ones it doesn't trust rather than re-picking from zero.
	const [selected, setSelected] = useState(() => {
		const init = new Set();
		for (const p of rows) if (isResponded(p)) init.add(p.id || p.name);
		return init;
	});

	// Single source of truth for "which quotes to share": the curation here.
	// Report the selected freight_quotes ids up so a typed share ("share
	// quotes to <email>") sends exactly what's ticked — same set the Share
	// button uses. Without this, the backend has no view of this selection
	// and would have to guess.
	useEffect(() => {
		if (typeof onSelectionChange !== "function") return;
		const ids = rows
			.filter(isResponded)
			.filter((p) => selected.has(p.id || p.name))
			.map((p) => p.quoteId)
			.filter(Boolean);
		onSelectionChange(ids);
	}, [rows, selected, onSelectionChange]);

	if (rows.length < 2 || responded.length === 0) return null;

	const toggle = (id) =>
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	const toggleShare = (id) =>
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	const { sorted, flagCtx } = analyzeProviders(rows);
	const sortedResponded = sorted.filter(isResponded);

	const hasSelection = rows.some((p) => p.status === "selected");
	// Share/curate mode owns the Decide state: when a share handler is wired and
	// nothing's been booked yet, dispatch curates + shares.
	const shareMode =
		typeof onShare === "function" && !selectionLocked && !hasSelection;
	const canAccept =
		typeof onAccept === "function" && !selectionLocked && !hasSelection;

	const selectedCount = sortedResponded.filter((p) =>
		selected.has(p.id || p.name),
	).length;

	// Cheapest among responded quotes — the one factual price highlight. (No
	// "fastest" — transit isn't always quoted, so it isn't a reliable signal.)
	const respondedCosts = sortedResponded
		.map(costOf)
		.filter((n) => n != null);
	const cheapestCost = respondedCosts.length ? Math.min(...respondedCosts) : null;

	// Dynamic optional columns — present only when ≥1 provider reported the field.
	const showTransit = sortedResponded.some((p) => p.days != null);
	const showValidity = sortedResponded.some((p) => p.validUntil);
	const showEquipment = sortedResponded.some((p) => p.equipment);
	const showRateType = sortedResponded.some((p) => rateTypeOf(p) != null);

	const cols = [{ key: "total", label: "TOTAL", w: "104px" }];
	if (showTransit) cols.push({ key: "transit", label: "TRANSIT", w: "72px" });
	if (showValidity)
		cols.push({ key: "valid", label: "VALID UNTIL", w: "92px" });
	if (showEquipment)
		cols.push({ key: "equipment", label: "EQUIPMENT", w: "104px" });
	if (showRateType)
		cols.push({ key: "rateType", label: "RATE TYPE", w: "92px" });

	// grid: checkbox + provider(1fr) + each value column
	const gridCols = `${shareMode ? "32px " : ""}minmax(0,1fr) ${cols
		.map((c) => c.w)
		.join(" ")}`;

	const cellVal = (p, key) => {
		if (key === "total")
			return <span className="ws-pcompare-total">{formatCost(p)}</span>;
		if (key === "transit")
			return p.days != null ? (
				<span className="ws-pcompare-cell">{p.days}d</span>
			) : (
				<span className="ws-pcompare-dash">—</span>
			);
		if (key === "valid")
			return p.validUntil ? (
				<span className="ws-pcompare-cell">{formatDate(p.validUntil)}</span>
			) : (
				<span className="ws-pcompare-dash">—</span>
			);
		if (key === "equipment")
			return p.equipment ? (
				<span className="ws-pcompare-cell">{p.equipment}</span>
			) : (
				<span className="ws-pcompare-dash">—</span>
			);
		if (key === "rateType") {
			const rt = rateTypeOf(p);
			return rt ? (
				<span className="ws-pcompare-cell is-muted">{rt}</span>
			) : (
				<span className="ws-pcompare-dash">—</span>
			);
		}
		return <span className="ws-pcompare-dash">—</span>;
	};

	const buildSharePayload = () => ({
		selected: sortedResponded
			.filter((p) => selected.has(p.id || p.name))
			.map((p) => ({
				id: p.id || p.name,
				// Real freight_quotes.id from the workspace data. The preview
				// harness ships mock rows without this field — handlers that
				// rely on quoteId must guard for it.
				quoteId: p.quoteId || null,
				name: p.name,
				cost: costOf(p),
				days: p.days ?? null,
			})),
	});

	return (
		<div className="rb rb--creator ws-pcompare">
			<div className="rb__head">
				<div className="rb__head-l ws-pcompare-counts">
					<span className="ws-pcompare-count-strong">
						{responded.length} quote{responded.length === 1 ? "" : "s"} received
					</span>
					{declined.length > 0 && (
						<>
							<span className="ws-pcompare-count-dot">·</span>
							<span className="ws-pcompare-count-muted">
								{declined.length} declined
							</span>
						</>
					)}
					{noReply.length > 0 && (
						<>
							<span className="ws-pcompare-count-dot">·</span>
							<span className="ws-pcompare-count-faint">
								{noReply.length} no reply
							</span>
						</>
					)}
				</div>
				<span className="rb__meta">Updated 2 min ago</span>
			</div>

			<div className="rb__body rb__body--flush">
				{/* Column headers */}
				<div
					className="ws-pcompare-row ws-pcompare-headrow"
					style={{ gridTemplateColumns: gridCols }}
				>
					{shareMode && <span />}
					<span className="ws-pcompare-colhead">PROVIDER</span>
					{cols.map((c) => (
						<span key={c.key} className="ws-pcompare-colhead">
							{c.label}
						</span>
					))}
				</div>

				{/* Responded quote rows */}
				{sortedResponded.map((p) => {
					const rowId = p.id || p.name;
					const open = expandedIds.has(rowId);
					const isChecked = selected.has(rowId);
					const isDropped = shareMode && !isChecked;
					const isOutlier = flagCtx.belowClusterId === rowId;
					const isCheapest =
						cheapestCost != null && costOf(p) === cheapestCost;
					const isSelectedRow = p.status === "selected";
					const accepting =
						acceptingQuoteId != null && acceptingQuoteId === p.quoteId;
					const hasBreakdown =
						Array.isArray(p.breakdown) && p.breakdown.length > 0;

					return (
						<Fragment key={rowId}>
							<div
								className={`ws-pcompare-quoterow${isDropped ? " is-dropped" : ""}${isOutlier && !isDropped ? " is-outlier" : ""}${isSelectedRow ? " is-selected" : ""}`}
							>
								<div
									className="ws-pcompare-row"
									style={{ gridTemplateColumns: gridCols }}
								>
									{shareMode && (
										<button
											type="button"
											className={`ws-pcompare-check${isChecked ? " is-on" : ""}`}
											onClick={() => toggleShare(rowId)}
											aria-label={
												isChecked
													? `Remove ${p.name} from the shortlist`
													: `Add ${p.name} to the shortlist`
											}
											aria-pressed={isChecked}
										>
											{isChecked && <Check size={12} strokeWidth={3} />}
										</button>
									)}

									<div className="ws-pcompare-provider">
										<div className="ws-pcompare-provider-main">
											<span className="ws-pcompare-name">{p.name}</span>
											{isSelectedRow && (
												<Pill tone="green">Accepted</Pill>
											)}
											{isCheapest && <Pill tone="green">Lowest price</Pill>}
											{isOutlier && (
												<Pill tone="orange">
													<AlertTriangle size={10} strokeWidth={2.2} /> Outlier
												</Pill>
											)}
										</div>
										{isOutlier && (
											<span className="ws-pcompare-outlier-note">
												{flagCtx.belowClusterPct}% below the next cheapest. No
												fuel breakdown or equipment specified.
											</span>
										)}
										<button
											type="button"
											className="ws-pcompare-showdetails"
											aria-expanded={open}
											onClick={() => toggle(rowId)}
										>
											{open ? (
												<ChevronUp size={12} strokeWidth={2.2} />
											) : (
												<ChevronDown size={12} strokeWidth={2.2} />
											)}
											{open ? "Hide details" : "Show details"}
										</button>
									</div>

									{cols.map((c) => (
										<div key={c.key} className="ws-pcompare-valuecell">
											{cellVal(p, c.key)}
										</div>
									))}
								</div>

								{/* Expanded: rate breakdown + provider's raw reply + actions */}
								{open && (
									<div className="ws-pcompare-detail">
										{hasBreakdown && (
											<div className="ws-pcompare-breakdown">
												{p.breakdown.map(([label, value], i) => (
													<BreakdownItem key={i} label={label} value={value} />
												))}
												<BreakdownItem
													label="Total"
													value={formatCost(p)}
													bold
												/>
											</div>
										)}

										<div className="ws-pcompare-reply-block">
											<div className="ws-pcompare-detail-title">
												Provider's reply
											</div>
											<div className="ws-pcompare-reply-box">
												<pre className="ws-pcompare-reply-text">
													{p.rawReply || "No reply text on file."}
												</pre>
											</div>
											<div className="ws-pcompare-reply-foot">
												<span className="ws-pcompare-extracted">
													<Mail size={12} strokeWidth={1.75} /> Auto-extracted
													from email
												</span>
												<div className="ws-pcompare-reply-actions">
													<button
														type="button"
														className="ws-btn ws-btn--outline ws-btn--small"
														disabled={
															!p.quoteId ||
															viewingPdfQuoteId === p.quoteId
														}
														onClick={() =>
															onViewPdf?.({ quoteId: p.quoteId })
														}
													>
														<FileText size={13} strokeWidth={1.75} />{" "}
														{viewingPdfQuoteId === p.quoteId
															? "Opening…"
															: "View PDF"}
													</button>
													{canAccept && p.quoteId && (
														<button
															type="button"
															className="ws-btn ws-btn--dark ws-btn--small"
															disabled={!!acceptingQuoteId}
															onClick={() =>
																onAccept({
																	quoteId: p.quoteId,
																	provider: p.name,
																})
															}
														>
															{accepting ? "Accepting…" : "Accept"}
														</button>
													)}
												</div>
											</div>
										</div>
									</div>
								)}
							</div>
						</Fragment>
					);
				})}

				{/* No reply — basic dash, no icon */}
				{noReply.map((p) => (
					<div key={p.id || p.name} className="ws-pcompare-status-row">
						<span className="ws-pcompare-status-name">{p.name}</span>
						<span className="ws-pcompare-status-note">—</span>
					</div>
				))}

				{/* Declined — explicit decline, no icon */}
				{declined.map((p) => (
					<div key={p.id || p.name} className="ws-pcompare-status-row">
						<span className="ws-pcompare-status-name">{p.name}</span>
						<span className="ws-pcompare-status-note">
							Declined{p.declinedReason ? ` — ${p.declinedReason}` : ""}
						</span>
					</div>
				))}
			</div>

			<div className="rb__foot ws-pcompare-foot">
				{shareMode ? (
					<>
						<span className="ws-pcompare-selectcount">
							<strong>
								{selectedCount} of {sortedResponded.length}
							</strong>{" "}
							selected
						</span>
						<div className="ws-pcompare-foot-actions">
							<button
								type="button"
								className="ws-btn ws-btn--primary ws-btn--small"
								disabled={selectedCount === 0}
								onClick={() => onShare(buildSharePayload())}
							>
								<Share2 size={14} strokeWidth={1.75} /> Share
							</button>
						</div>
					</>
				) : (
					<span className="rb__meta">
						Auto-extracted from provider replies · expand a row to verify
						against the original reply
					</span>
				)}
			</div>
		</div>
	);
}
